import {
  Box,
  type BoxProps,
  Input,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { ChevronDown } from 'lucide-react'
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import type { ApiError, PbxSettingsApi } from '../api/pbxSettingsApi.types'
import type { PbxSettingsTranslateFn } from '../types'
import { text } from '../types'
import type {
  Company,
  PaginatedCompanyListResponse,
} from '../types/pbxSettings.types'

const DEBOUNCE_DELAY_MS = 300
const COMPANY_PAGE_SIZE = 100
const MENU_MAX_HEIGHT = '300px'
const inputFocusStyles = {
  bg: 'bg.subtle',
  borderColor: 'border.default',
  borderRadius: 'lg',
  _focusVisible: {
    borderColor: 'blue.500',
    boxShadow: '0 0 0 2px var(--chakra-colors-blue-500)',
  },
}

export interface PbxSettingsCompanySelectProps extends Omit<
  BoxProps,
  'onChange'
> {
  api: PbxSettingsApi
  t?: PbxSettingsTranslateFn
  selectedCompanyId?: number
  selectedCompanyName?: string
  onCompanyChange?: (company: Company | null) => void
  /** Notifies the parent of the company resolved for the current
   * company_id from the companies list (i.e. the Company page record). */
  onResolvedCompany?: (company: Company | null) => void
  disabled?: boolean
  placeholder?: string
}

/**
 * PBX-page-specific company selector.
 * Kept separate from shared CompanySelect to avoid cross-page behavior coupling.
 */
export const PbxSettingsCompanySelect = memo(
  ({
    api,
    t,
    selectedCompanyId,
    selectedCompanyName,
    onCompanyChange,
    onResolvedCompany,
    disabled = false,
    placeholder,
    ...rest
  }: PbxSettingsCompanySelectProps) => {
    const [searchTerm, setSearchTerm] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [isOpen, setIsOpen] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      const timer = setTimeout(() => {
        setDebouncedSearch(searchTerm)
      }, DEBOUNCE_DELAY_MS)

      return () => clearTimeout(timer)
    }, [searchTerm])

    const {
      data: companiesPages,
      isLoading,
      isFetchingNextPage,
      hasNextPage,
      fetchNextPage,
    } = useInfiniteQuery<PaginatedCompanyListResponse, ApiError>({
      queryKey: [
        'edit-pbx-companies',
        'infinite',
        { search: debouncedSearch || '', limit: COMPANY_PAGE_SIZE },
      ],
      initialPageParam: 0,
      queryFn: async ({ pageParam }) => {
        const offset =
          typeof pageParam === 'number'
            ? pageParam
            : Number.parseInt(String(pageParam), 10) || 0

        const { ok, data } = await api.getCompaniesList({
          search: debouncedSearch || undefined,
          limit: COMPANY_PAGE_SIZE,
          offset,
        })

        if (!ok) {
          return Promise.reject(data as ApiError)
        }

        return data as PaginatedCompanyListResponse
      },
      getNextPageParam: (lastPage, allPages) => {
        const loadedCount = allPages.reduce(
          (sum, page) => sum + page.results.length,
          0,
        )
        return loadedCount < lastPage.count ? loadedCount : undefined
      },
      retry: false,
    })

    const [persistedSelectedCompany, setPersistedSelectedCompany] =
      useState<Company | null>(null)

    const { companies, companyLookup } = useMemo(() => {
      const results =
        companiesPages?.pages.flatMap((page) => page.results) ?? []
      const lookup: Record<string, Company> = {}

      results.forEach((company) => {
        lookup[company.id.toString()] = company
      })

      if (persistedSelectedCompany) {
        lookup[persistedSelectedCompany.id.toString()] =
          persistedSelectedCompany
      }

      return { companies: results, companyLookup: lookup }
    }, [companiesPages, persistedSelectedCompany])

    const selectedCompany = selectedCompanyId
      ? companyLookup[selectedCompanyId.toString()]
      : undefined

    useEffect(() => {
      if (selectedCompany) {
        setPersistedSelectedCompany(selectedCompany)
      } else if (!selectedCompanyId) {
        setPersistedSelectedCompany(null)
      }
    }, [selectedCompany, selectedCompanyId])

    useEffect(() => {
      onResolvedCompany?.(selectedCompany ?? null)
    }, [onResolvedCompany, selectedCompany])

    const handleCompanySelected = useCallback(
      (companyId: string) => {
        const company = companyLookup[companyId]
        if (!company) return

        onCompanyChange?.(company)
        setIsOpen(false)
        setSearchTerm('')
      },
      [companyLookup, onCompanyChange],
    )

    const handleInputFocus = useCallback(() => {
      if (!disabled) {
        setIsOpen(true)
      }
    }, [disabled])

    const isFocusInsideSelect = useCallback((target: EventTarget | null) => {
      if (!target) return false
      const node = target as Node
      return Boolean(
        inputRef.current?.contains(node) || dropdownRef.current?.contains(node),
      )
    }, [])

    const handleInputBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        if (!isFocusInsideSelect(e.relatedTarget)) {
          setIsOpen(false)
        }
      },
      [isFocusInsideSelect],
    )

    const handleSearchBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        if (!isFocusInsideSelect(e.relatedTarget)) {
          setIsOpen(false)
        }
      },
      [isFocusInsideSelect],
    )

    const handleSearchKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
          e.preventDefault()
          setIsOpen(false)
          inputRef.current?.focus()
        }
      },
      [],
    )

    const inputValue = useMemo(() => {
      return selectedCompany?.name ?? selectedCompanyName ?? ''
    }, [selectedCompany, selectedCompanyName])

    useEffect(() => {
      if (isOpen) {
        const timer = setTimeout(() => searchInputRef.current?.focus(), 0)
        return () => clearTimeout(timer)
      }
      return undefined
    }, [isOpen])

    useEffect(() => {
      if (!isOpen) return

      const handlePointerDown = (event: MouseEvent) => {
        const target = event.target as Node
        const clickedInput = inputRef.current?.contains(target)
        const clickedDropdown = dropdownRef.current?.contains(target)
        if (!clickedInput && !clickedDropdown) {
          setIsOpen(false)
        }
      }

      document.addEventListener('mousedown', handlePointerDown)
      return () => document.removeEventListener('mousedown', handlePointerDown)
    }, [isOpen])

    const emptyStateText = useMemo(() => {
      if (isLoading) {
        return text(
          t,
          'pbxSettings.companySelect.empty.loading',
          'Loading companies...',
        )
      }
      if (searchTerm) {
        return text(
          t,
          'pbxSettings.companySelect.empty.noResults',
          'No companies found',
        )
      }
      return text(
        t,
        'pbxSettings.companySelect.empty.typeToSearch',
        'Type to search companies',
      )
    }, [isLoading, searchTerm, t])

    const resolvedPlaceholder = useMemo(
      () =>
        placeholder ??
        text(t, 'pbxSettings.companySelect.placeholder', 'Select Company'),
      [placeholder, t],
    )

    return (
      <Box position="relative" width="100%" {...rest}>
        <Box position="relative">
          <Input
            {...inputFocusStyles}
            ref={inputRef}
            value={inputValue}
            onClick={handleInputFocus}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            placeholder={resolvedPlaceholder}
            disabled={disabled}
            readOnly
            px={3}
            cursor={disabled ? 'not-allowed' : 'pointer'}
            role="combobox"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-label={text(
              t,
              'pbxSettings.companySelect.ariaLabel',
              'Company selector',
            )}
            aria-autocomplete="list"
            pr={10}
          />
          <Box
            position="absolute"
            right={3}
            top="50%"
            transform="translateY(-50%)"
            pointerEvents="none"
            color="fg.muted"
          >
            <ChevronDown size={16} />
          </Box>
        </Box>
        {isOpen && (
          <Box
            ref={dropdownRef}
            position="absolute"
            top="calc(100% + 4px)"
            left={0}
            width="100%"
            zIndex={1500}
            bg="bg.panel"
            borderWidth="1px"
            borderColor="border.default"
            borderRadius="lg"
            shadow="lg"
            maxHeight={MENU_MAX_HEIGHT}
            overflowY="auto"
            onScroll={(event) => {
              if (!hasNextPage || isFetchingNextPage) return
              const target = event.currentTarget
              const nearBottom =
                target.scrollTop + target.clientHeight >=
                target.scrollHeight - 12
              if (nearBottom) {
                void fetchNextPage()
              }
            }}
          >
            <Box
              position="sticky"
              top={0}
              zIndex={1}
              bg="bg.panel"
              borderBottomWidth="1px"
              borderColor="border.default"
              p={2}
            >
              <Input
                {...inputFocusStyles}
                ref={searchInputRef}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onBlur={handleSearchBlur}
                onKeyDown={handleSearchKeyDown}
                placeholder={text(
                  t,
                  'pbxSettings.companySelect.searchPlaceholder',
                  'Search companies...',
                )}
                size="sm"
                onMouseDown={(e) => {
                  e.stopPropagation()
                }}
              />
            </Box>
            {companies.length === 0 ? (
              isLoading ? (
                <Box p={3}>
                  <VStack align="stretch" gap={2}>
                    <Skeleton height="16px" />
                    <Skeleton height="16px" />
                    <Skeleton height="16px" />
                  </VStack>
                </Box>
              ) : (
                <Box p={4} textAlign="center" color="fg.muted">
                  <Text fontSize="sm">{emptyStateText}</Text>
                </Box>
              )
            ) : (
              companies.map((company) => (
                <Box
                  key={company.id}
                  px={4}
                  py={2}
                  cursor="pointer"
                  bg={
                    selectedCompanyId === company.id ? 'blue.50' : 'transparent'
                  }
                  _hover={{ bg: 'gray.50' }}
                  onClick={() => handleCompanySelected(company.id.toString())}
                  onMouseDown={(e) => {
                    e.preventDefault()
                  }}
                >
                  <VStack align="start" gap={0}>
                    <Text fontWeight="medium">{company.name}</Text>
                  </VStack>
                </Box>
              ))
            )}
            {isFetchingNextPage && (
              <Box p={3}>
                <Skeleton height="16px" />
              </Box>
            )}
          </Box>
        )}
      </Box>
    )
  },
)
